import React, { useState } from "react";
import { Controller, useFormContext, RegisterOptions } from "react-hook-form";
import TextField, {
  TextFieldProps as MuiTextFieldProps,
} from "@mui/material/TextField";
import { Box, IconButton, InputAdornment, SxProps } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

interface MTextFieldProps extends Omit<MuiTextFieldProps, "name"> {
  name: string;
  validation?: RegisterOptions;
  label?: string;
  stylesInput?: SxProps;
  step?: string | number;
}

// Función para procesar el valor ingresado por el usuario
export const handleInputChange = (value: string) => {
  if (!value) return "";

  const normalizedValue = value.replace(/e/g, "E");
  const scientificNotationRegex = /^-?\d+(\.\d+)?(E[+-]?\d+)?$/;

  if (scientificNotationRegex.test(normalizedValue)) {
    return normalizedValue;
  }

  const cleanedValue = normalizedValue.replace(/[^0-9Ee+.-]/g, "");
  return cleanedValue;
};

const MTextField: React.FC<MTextFieldProps> = ({
  name,
  label,
  validation,
  stylesInput,
  type,
  step,
  ...props
}) => {
  const { control } = useFormContext();
  const { disabled } = props;
  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const isPasswordField = type === "password";

  // Renderización de campo numérico
  const renderNumberInput = (field: any, fieldState: any) => (
    <TextField
      {...field}
      {...props}
      type="text"
      error={!!fieldState.error}
      label={label && <span dangerouslySetInnerHTML={{ __html: label }} />}
      helperText={
        fieldState.error ? fieldState.error.message : props.helperText
      }
      inputProps={{
        ...props.inputProps,
        step: step,
      }}
      variant={disabled ? "filled" : "outlined"}
      sx={{ ...stylesInput }}
      InputProps={props.InputProps}
      value={field.value ?? ""}
      onChange={(e) => {
        const formattedValue = handleInputChange(e.target.value);
        field.onChange(formattedValue);
      }}
    />
  );

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    field: any
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      field.onChange(file);
    }
  };

  // Renderizar input de tipo file
  const renderFileInput = (field: any, fieldState: any) => (
    <Box>
      <TextField
        {...props}
        type="file"
        error={!!fieldState.error}
        label={label && <span dangerouslySetInnerHTML={{ __html: label }} />}
        helperText={
          fieldState.error ? fieldState.error.message : props.helperText
        }
        inputProps={{ ...props.inputProps, step: step }}
        variant={disabled ? "filled" : "outlined"}
        sx={{ ...stylesInput }}
        onChange={(e: any) => handleFileChange(e, field)}
      />
    </Box>
  );

  // Renderización de campo estándar (no numérico)
  const renderStandardInput = (field: any, fieldState: any) => (
    <TextField
      {...field}
      {...props}
      type={isPasswordField && showPassword ? "text" : type}
      error={!!fieldState.error}
      label={label && <span dangerouslySetInnerHTML={{ __html: label }} />}
      helperText={
        fieldState.error ? fieldState.error.message : props.helperText
      }
      inputProps={{
        ...props.inputProps,
        step: step,
      }}
      variant={disabled ? "filled" : "outlined"}
      sx={{ ...stylesInput }}
      InputProps={{
        ...props.InputProps,
        endAdornment: isPasswordField ? (
          <InputAdornment position="end">
            <IconButton
              aria-label="toggle password visibility"
              onClick={handleClickShowPassword}
              edge="end"
            >
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        ) : (
          props.InputProps?.endAdornment
        ),
      }}
      value={field.value ?? ""}
    />
  );

  return (
    <Controller
      name={name}
      control={control}
      rules={validation}
      render={({ field, fieldState }) =>
        type === "number"
          ? renderNumberInput(field, fieldState)
          : type === "file"
          ? renderFileInput(field, fieldState)
          : renderStandardInput(field, fieldState)
      }
    />
  );
};

export default MTextField;
